import {
  useMutationMemberInvoiceAsMember,
  useMutationPayInvoiceAsMember,
  useQueryMemberHighlightedInvoiceAsMember,
} from "@gymflow/api";
import { downloadHtml, formatCurrency } from "@gymflow/helpers";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import useScaModal from "../../../../hooks/useScaModal";
import { useClubSettings } from "../../../../providers";
import useGymflowModels from "../../../../store";
import { Button, DownloadIcon } from "../../../atoms";
import CustomTooltip from "../../../atoms/CustomTooltip";
import { CalendarWithClockIcon } from "../../../atoms/icons/CalendarWithClockIcon";
import { ExclamationCircleIcon } from "../../../atoms/icons/ExclamationCircleIcon";
import { ShieldIcon } from "../../../atoms/icons/ShieldIcon";
import { HostedPagesProfileCard } from "../../components/HostedPagesProfileCard";

export type HostedPagesHighlightedInvoiceProps = {};
export const HostedPagesHighlightedInvoice =
  ({}: HostedPagesHighlightedInvoiceProps) => {
    const { t } = useTranslation();
    const { api } = useGymflowModels();
    const settings = useClubSettings();
    const { data: highlightedInvoice } =
      useQueryMemberHighlightedInvoiceAsMember({ api });

    const { mutateAsync: downloadInvoice } = useMutationMemberInvoiceAsMember({
      api,
      clubId: settings.clubId,
    });
    const { mutateAsync: payInvoice } = useMutationPayInvoiceAsMember({
      api,
    });
    const showScaModal = useScaModal({ asMember: true });

    return (
      <HostedPagesProfileCard.Container className="flex flex-col">
        <div className="flex w-full flex-row items-center justify-between">
          {highlightedInvoice?.status === "PAST_DUE" && (
            <CustomTooltip
              message={t(
                `StripeErrorCodes.Long.${
                  highlightedInvoice.lastPaymentErrorCode ?? "UNKNOWN"
                }`,
              )}
            >
              <div className="bg-error-100 text-error-600 flex flex-row items-center justify-center gap-2 rounded-full px-3 py-1 font-semibold">
                <ExclamationCircleIcon className="stroke-error-600 h-5 w-5" />
                {t(
                  `StripeErrorCodes.Short.${
                    highlightedInvoice.lastPaymentErrorCode ?? "UNKNOWN"
                  }`,
                )}
              </div>
            </CustomTooltip>
          )}

          {highlightedInvoice?.status === "AWAITING_AUTHORIZATION" && (
            <div className="bg-warning-100 text-warning-600 flex flex-row items-center justify-center gap-2 rounded-full px-3 py-1 font-semibold">
              <ShieldIcon className="h-5 w-5" />
              {t("pages.hostedPagesProfileReceipts.authorizationNeeded")}
            </div>
          )}
          {(!highlightedInvoice ||
            highlightedInvoice?.status === "SCHEDULED") && (
            <div className="flex flex-row items-center gap-2">
              <div className="bg-secondary-100 flex h-9 w-9 items-center justify-center rounded-full p-2">
                <CalendarWithClockIcon className="fill-secondary-500" />
              </div>
              <div className="dark:text-gray-0 flex font-semibold text-gray-950">
                {t("pages.hostedPagesProfileReceipts.scheduledInvoice")}
              </div>
            </div>
          )}
          {highlightedInvoice && (
            <Button
              intent="transparent"
              className="h-8 min-h-0 justify-start gap-2"
              onClick={async () => {
                const { number } = highlightedInvoice;
                const { data: html } = await downloadInvoice({
                  invoiceNumber: number,
                });
                downloadHtml(html, `Gymflow-${number}.html`);
              }}
            >
              <DownloadIcon pathClassName="stroke-gray-500" />
              {t("pages.hostedPagesProfileReceipts.downloadInvoice")}
            </Button>
          )}
        </div>
        <div className="flex w-full flex-row items-center justify-between">
          {!highlightedInvoice && (
            <div className="text-lg font-medium text-gray-500">
              {t("pages.hostedPagesProfileReceipts.noInvoices")}
            </div>
          )}
          {highlightedInvoice && (
            <>
              <div className="flex flex-col">
                <div className="dark:text-gray-0 text-2xl font-bold text-gray-950">
                  {formatCurrency(
                    highlightedInvoice.totalAmount,
                    settings.defaultCurrency,
                  )}
                </div>
                <div className="text-sm font-medium text-gray-500">
                  {DateTime.fromISO(
                    highlightedInvoice.issueDate,
                  ).toLocaleString(DateTime.DATE_MED)}
                </div>
              </div>

              {highlightedInvoice.status === "PAST_DUE" && (
                <Button
                  onClick={async () => {
                    await payInvoice({
                      invoiceNumber: highlightedInvoice.number,
                    });
                    showScaModal({
                      invoiceNumber: highlightedInvoice.number,
                    });
                  }}
                >
                  {t("pages.hostedPagesProfileReceipts.pay")}
                </Button>
              )}
              {highlightedInvoice.status === "AWAITING_AUTHORIZATION" && (
                <Button
                  className="min-h-0 min-w-0 gap-1"
                  onClick={async () => {
                    await showScaModal({
                      invoiceNumber: highlightedInvoice.number,
                    });
                  }}
                >
                  {t("pages.hostedPagesProfileReceipts.approve")}
                </Button>
              )}
            </>
          )}
        </div>
      </HostedPagesProfileCard.Container>
    );
  };
